/** Recruitment Network · optional candidate details (notice, CTC, visa — migration 0090).
 *
 *  Shows only the fields this job has switched on. For each: what the CANDIDATE reported on their
 *  "complete your application" page, and what the RECRUITER recorded — kept side by side, never
 *  merged. The "Fields" menu picks which fields are on, for this job or as the org default. */
import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Settings2, Pencil } from "lucide-react";
import { CANDIDATE_FIELD_KEYS, CANDIDATE_FIELD_META } from "@talenthop/shared";
import type { CandidateDetails, CandidateFieldKey } from "@talenthop/shared";
import { api, ApiError } from "../../../lib/api.js";
import { useAuth } from "../../../lib/AuthContext.js";
import { useReportSource } from "../reportSource.js";

interface FieldsResponse {
  orgDefault: CandidateFieldKey[];
  jobOverride: CandidateFieldKey[] | null;
  effective: CandidateFieldKey[];
}
interface DetailsResponse extends FieldsResponse {
  recruiter: CandidateDetails;
  recruiterUpdatedAt: string | null;
  submitted: CandidateDetails;
  /** The candidate's own answers from "complete your application" (absent on an older API). */
  profile?: {
    about?: string;
    skills?: string[];
    graduationYear?: number;
    experienceYears?: number;
    location?: string;
    lastWorkingDate?: string;
    phone?: string;
    linkedinUrl?: string;
    submittedAt?: string;
  };
  /** What a forwarded application email (e.g. a Naukri notification) said about them. */
  fromEmail?: FromEmail | null;
  /** What the recruiter's imported spreadsheet said about them (candidatesImport.ts). */
  fromImport?: FromImport | null;
}
interface FromImport {
  file?: string | null;
  importedAt?: string;
  phone?: string;
  experienceYears?: number;
  lastWorkingDate?: string;
  location?: string;
  extra?: Record<string, string>;
}
interface FromEmail {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  linkedinUrl?: string | null;
  location?: string | null;
  experienceYears?: number | null;
  currentRole?: string | null;
  currentCompany?: string | null;
  noticePeriod?: string | null;
  skills?: string[];
  readBy?: "ai" | "rules";
  subject?: string | null;
  at?: string;
}

function show(k: CandidateFieldKey, v: CandidateDetails[CandidateFieldKey]): string {
  if (v === undefined || v === null || v === "") return "—";
  if (k === "noticePeriodDays") return v === 0 ? "Immediate" : `${v} days`;
  if (k === "visaNeeded") return v ? "Yes" : "No";
  return String(v);
}

export function CandidateDetailsFields({ candidacyId, reqId }: { candidacyId: string; reqId: string }): ReactNode {
  // On a shared report: the share-scoped read; no record / field-settings controls.
  const { shareToken, readOnly } = useReportSource();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const q = useQuery({
    queryKey: ["candidate-details", shareToken ?? candidacyId],
    queryFn: () =>
      api.get<DetailsResponse>(shareToken ? `/shared/${shareToken}/details` : `/candidacies/${candidacyId}/details`),
    retry: shareToken ? false : undefined,
  });

  const save = useMutation({
    mutationFn: (values: CandidateDetails) => api.put(`/candidacies/${candidacyId}/details`, values),
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ["candidate-details", candidacyId] });
    },
  });

  if (q.isLoading) return <p className="rn-muted">Loading details…</p>;
  // A redacted share withholds these (404) — say nothing rather than show an error.
  if (readOnly && (q.isError || !q.data)) return null;
  if (q.isError || !q.data) return <p className="rn-muted">Couldn’t load candidate details.</p>;
  const d = q.data;

  const startEdit = (): void => {
    const init: Record<string, string> = {};
    for (const k of d.effective) {
      const v = d.recruiter[k];
      init[k] = v === undefined || v === null ? "" : k === "visaNeeded" ? (v ? "yes" : "no") : String(v);
    }
    setDraft(init);
    setEditing(true);
  };

  const submit = (): void => {
    const out: Record<string, unknown> = {};
    for (const k of d.effective) {
      const raw = (draft[k] ?? "").trim();
      if (k === "noticePeriodDays") out[k] = raw === "" ? null : Math.max(0, Math.min(365, Math.round(Number(raw))));
      else if (k === "visaNeeded") out[k] = raw === "" ? null : raw === "yes";
      else out[k] = raw === "" ? null : raw;
    }
    save.mutate(out as CandidateDetails);
  };

  return (
    <section className="rn-cd">
      <div className="rn-cd-head">
        <h4>Candidate details</h4>
        {!readOnly && <div className="rn-cd-actions">
          {d.effective.length > 0 && !editing && (
            <button type="button" className="rn-icon-btn" onClick={startEdit} data-tip="Record details" data-tip-pos="left" aria-label="Record details">
              <Pencil size={14} />
            </button>
          )}
          <button
            type="button"
            className="rn-icon-btn"
            onClick={() => setConfigOpen((o) => !o)}
            aria-expanded={configOpen}
            data-tip="Choose which details this job collects"
            data-tip-pos="left"
            aria-label="Choose fields"
          >
            <Settings2 size={15} />
          </button>
        </div>}
      </div>

      {configOpen && <FieldsConfig reqId={reqId} current={d} onDone={() => setConfigOpen(false)} />}

      {d.effective.length === 0 ? (
        <p className="rn-muted sm">
          {readOnly
            ? "No detail fields are collected for this job."
            : "No detail fields are switched on for this job. Use the settings icon to add notice period, CTC or visa."}
        </p>
      ) : editing ? (
        <div className="rn-cd-form">
          {d.effective.map((k) => {
            const meta = CANDIDATE_FIELD_META[k];
            return (
              <label key={k}>
                <span>{meta.label}</span>
                {meta.kind === "yesno" ? (
                  <select className="rn-select" value={draft[k] ?? ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}>
                    <option value="">Not recorded</option>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                ) : (
                  <input
                    className="rn-input"
                    type={meta.kind === "days" ? "number" : "text"}
                    min={meta.kind === "days" ? 0 : undefined}
                    max={meta.kind === "days" ? 365 : undefined}
                    maxLength={60}
                    placeholder={meta.placeholder}
                    value={draft[k] ?? ""}
                    onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                  />
                )}
              </label>
            );
          })}
          <div className="rn-cd-buttons">
            {save.isError && <span className="rn-muted sm">Couldn’t save.</span>}
            <button type="button" className="rn-btn ghost" onClick={() => setEditing(false)}>Cancel</button>
            <button type="button" className="rn-btn" disabled={save.isPending} onClick={submit}>
              {save.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      ) : (
        <table className="rn-cd-table">
          <thead>
            <tr><th /><th>Recruiter</th><th>Candidate said</th></tr>
          </thead>
          <tbody>
            {d.effective.map((k) => (
              <tr key={k}>
                <th>{CANDIDATE_FIELD_META[k].label}</th>
                <td>{show(k, d.recruiter[k])}</td>
                <td className="rn-muted">{show(k, d.submitted[k])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <CandidateSaid p={d.profile} />
      <FromForwardedEmail f={d.fromEmail} />
      <FromImportedFile f={d.fromImport} />
      <p className="rn-muted sm">{readOnly ? "Context only" : "Context for your team only"} — never used in scoring or ranking.</p>
    </section>
  );
}

/** Which fields are on: for this job only (override) or for every job (org default, admins). */
function FieldsConfig({ reqId, current, onDone }: { reqId: string; current: FieldsResponse; onDone: () => void }): ReactNode {
  const qc = useQueryClient();
  const { me } = useAuth();
  const [scope, setScope] = useState<"job" | "org">("job");
  const [picked, setPicked] = useState<CandidateFieldKey[]>(current.effective);

  const apply = useMutation({
    mutationFn: (fields: CandidateFieldKey[] | null) =>
      scope === "org"
        ? api.put(`/orgs/${me!.orgId}/candidate-fields`, { fields: fields ?? [] })
        : api.put(`/requisitions/${reqId}/candidate-fields`, { fields }),
    onSuccess: () => {
      // every open panel re-reads what's in force
      void qc.invalidateQueries({ queryKey: ["candidate-details"] });
      onDone();
    },
  });

  const toggle = (k: CandidateFieldKey): void =>
    setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const err = apply.error instanceof ApiError && apply.error.status === 403
    ? "Only an org admin can change the org default."
    : apply.isError
      ? "Couldn’t save."
      : null;

  return (
    <div className="rn-cd-config">
      <div className="rn-seg" role="group" aria-label="Apply to">
        <button type="button" className={scope === "job" ? "is-on" : ""} onClick={() => setScope("job")}>This job</button>
        <button type="button" className={scope === "org" ? "is-on" : ""} onClick={() => setScope("org")}>All jobs (org default)</button>
      </div>
      {CANDIDATE_FIELD_KEYS.map((k) => (
        <label key={k} className="rn-check">
          <input type="checkbox" checked={picked.includes(k)} onChange={() => toggle(k)} />
          {CANDIDATE_FIELD_META[k].label}
        </label>
      ))}
      <p className="rn-muted sm">
        {scope === "job"
          ? current.jobOverride
            ? "This job has its own fields."
            : `This job uses the org default (${current.orgDefault.length ? current.orgDefault.map((k) => CANDIDATE_FIELD_META[k].label).join(", ") : "none"}).`
          : "Applies to every job that hasn’t chosen its own fields."}{" "}
        Candidates are asked for switched-on fields on their “complete your application” page.
      </p>
      {err && <p className="rn-muted sm">{err}</p>}
      <div className="rn-cd-buttons">
        {scope === "job" && current.jobOverride && (
          <button type="button" className="rn-btn ghost" disabled={apply.isPending} onClick={() => apply.mutate(null)}>
            Use org default
          </button>
        )}
        <button type="button" className="rn-btn ghost" onClick={onDone}>Cancel</button>
        <button type="button" className="rn-btn" disabled={apply.isPending} onClick={() => apply.mutate(picked)}>
          {apply.isPending ? "Saving…" : "Apply"}
        </button>
      </div>
    </div>
  );
}

/** What a forwarded application email said — read-only, and labelled as coming from that email. */
function FromForwardedEmail({ f }: { f?: FromEmail | null }): ReactNode {
  if (!f) return null;
  const rows: [string, string][] = [];
  if (f.email) rows.push(["Email", f.email]);
  if (f.phone) rows.push(["Phone", f.phone]);
  if (f.experienceYears != null) rows.push(["Experience", `${f.experienceYears} yrs`]);
  if (f.currentRole || f.currentCompany) rows.push(["Current", [f.currentRole, f.currentCompany].filter(Boolean).join(" · ")]);
  if (f.location) rows.push(["Location", f.location]);
  if (f.noticePeriod) rows.push(["Notice", f.noticePeriod]);
  if (f.linkedinUrl) rows.push(["LinkedIn", f.linkedinUrl.replace(/^https?:\/\/(www\.)?/i, "")]);
  if (f.skills?.length) rows.push(["Skills", f.skills.join(", ")]);
  if (!rows.length) return null;
  return (
    <div className="rn-cd-said">
      <h5>
        From the forwarded email{f.at ? ` · ${new Date(f.at).toLocaleDateString()}` : ""}
        {f.readBy === "ai" ? " · read by AI" : ""}
      </h5>
      {f.subject && <p className="rn-muted sm">{f.subject}</p>}
      <table className="rn-cd-table">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}><th>{k}</th><td className="rn-muted">{v}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** What the imported spreadsheet said — read-only, and labelled with the file it came from. Notice
 *  period and CTC from the file are in the editable table above (they're the team's own fields). */
function FromImportedFile({ f }: { f?: FromImport | null }): ReactNode {
  if (!f) return null;
  const rows: [string, string][] = [];
  if (f.phone) rows.push(["Phone", f.phone]);
  if (f.experienceYears != null) rows.push(["Experience", `${f.experienceYears} yrs`]);
  if (f.location) rows.push(["Location", f.location]);
  if (f.lastWorkingDate) rows.push(["Last working day", new Date(`${f.lastWorkingDate}T00:00:00`).toLocaleDateString()]);
  for (const [k, v] of Object.entries(f.extra ?? {})) rows.push([k, v]);
  if (!rows.length) return null;
  return (
    <div className="rn-cd-said">
      <h5>
        From your imported file{f.file ? ` · ${f.file}` : ""}
        {f.importedAt ? ` · ${new Date(f.importedAt).toLocaleDateString()}` : ""}
      </h5>
      <table className="rn-cd-table">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}><th>{k}</th><td className="rn-muted">{v}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** What the candidate wrote about themselves — read-only, and labelled as theirs. */
function CandidateSaid({ p }: { p?: DetailsResponse["profile"] }): ReactNode {
  if (!p) return null;
  const rows: [string, ReactNode][] = [];
  if (p.phone) rows.push(["Phone", p.phone]);
  if (p.linkedinUrl) rows.push(["LinkedIn", p.linkedinUrl.replace(/^https?:\/\/(www\.)?/i, "")]);
  if (p.skills?.length) rows.push(["Skills", p.skills.join(", ")]);
  if (p.graduationYear) rows.push(["Graduation", String(p.graduationYear)]);
  if (p.experienceYears !== undefined) rows.push(["Experience", `${p.experienceYears} yrs`]);
  if (p.location) rows.push(["Location", p.location]);
  if (p.lastWorkingDate) rows.push(["Last working day", new Date(`${p.lastWorkingDate}T00:00:00`).toLocaleDateString()]);
  if (!rows.length && !p.about) return null;
  return (
    <div className="rn-cd-said">
      <h5>In their own words{p.submittedAt ? ` · ${new Date(p.submittedAt).toLocaleDateString()}` : ""}</h5>
      {p.about && <p>{p.about}</p>}
      {rows.length > 0 && (
        <table className="rn-cd-table">
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k}><th>{k}</th><td className="rn-muted">{v}</td></tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
